import { Domain, PartialPropertyUpdateTree, Path } from "@animech/axiom-format";
import {
	getQualifiedPropertyName,
	isInSubtree,
	PropertyMap,
} from "@animech/axiom-utility";
import {
	Configurator,
	ConfiguratorChange,
	fieldDomainToPropertyUpdate,
	fieldValueToPropertyUpdate,
	propertyDomainToFieldValues,
} from "src/utility";
import { Root, root } from "src/definitions";
import {
	ConfigurationHandler,
	DefaultConfigurationHandler,
	Property,
	PropertyValue,
} from "src/common";

export type ConfigurationChangeListener = (
	setProperty: Property,
	change: ConfiguratorChange,
) => void;

/**
 * Thin adapter between the app and the Axiom {@link Configurator}.
 *
 * IMPORTANT — these methods are effectively SYNCHRONOUS despite their `Promise`
 * return types. The `Promise` signatures exist only to satisfy
 * `ConfiguratorInterface` (`@animech/axiom-ui-builder`), which
 * `ConfiguratorController` requires (see `ui-manager.tsx`). With the default
 * configuration handler — the only handler this app registers — {@link set}
 * applies the change and fires every {@link ConfigurationChangeListener} (and
 * their side effects: scene rebuild, price, render, history) BEFORE it returns;
 * the returned promise is already resolved. Callers therefore do not need to
 * `await` for ordering, and intentionally don't: undo/redo, boot and
 * stored-config loading call the bridge fire-and-forget and rely on this
 * synchronous behaviour.
 *
 * Do NOT reintroduce `await handler.set(...)` on the default path in {@link set}.
 * Awaiting defers the listeners to a microtask, which runs selection and camera
 * updates against the pre-change scene — the regression locked down by the
 * synchronous-contract test (`configurator-bridge.test.ts`).
 *
 * The asynchrony is real only for a registered async custom handler; none exist
 * today. If one is ever added, the fire-and-forget call sites above must be
 * revisited to await it.
 */
export class ConfiguratorBridge {
	private _configurator: Configurator;
	private _configurationChangeListeners =
		new Set<ConfigurationChangeListener>();
	private _defaultHandler: ConfigurationHandler;
	private _customHandlers = new PropertyMap<Root, ConfigurationHandler>();

	constructor(
		configurator: Configurator,
		defaultHandler = new DefaultConfigurationHandler(configurator),
	) {
		this._configurator = configurator;
		this._defaultHandler = defaultHandler;
	}

	async registerHandler(
		property: Property,
		handler: ConfigurationHandler,
	): Promise<ConfiguratorChange> {
		if (
			[...this._customHandlers.keys()].some((p) => isInSubtree(property, p))
		) {
			throw new Error(
				`Handler already registered that covers property ${property}`,
			);
		}
		this._customHandlers.set(property, handler);
		const change = await handler.init(property);
		for (const listener of this._configurationChangeListeners) {
			listener(property, change);
		}
		return change;
	}

	async set(
		property: Property,
		tree: PartialPropertyUpdateTree<Property>,
	): Promise<ConfiguratorChange> {
		const handler = this._getHandler(property);
		// The default handler just wraps the *synchronous* configurator in an
		// async method. Applying it directly (no await) keeps the change
		// listeners — and their side effects: scene rebuild, price, render,
		// history — synchronous, so they complete before the caller continues.
		// Awaiting defers them to a microtask, which breaks ordering for
		// fire-and-forget callers (undo/redo, stored-config load): selection and
		// camera then run against the pre-change scene. Custom handlers, which
		// may do real async work, keep their asynchronous behaviour.
		const change =
			handler === this._defaultHandler
				? this._configurator.set(property, tree)
				: await handler.set(property, tree);
		for (const listener of this._configurationChangeListeners) {
			listener(property, change);
		}
		return change;
	}

	async getValue<TValue, TProperty extends Property<TValue>>(
		property: TProperty,
	): Promise<TValue> {
		const handler = this._getHandler(property);
		if (this._isMissingDefaultProperty(handler, property)) {
			return undefined as TValue;
		}
		const state = await handler.get(property);
		return state.value as TValue;
	}

	setValue(
		property: Property,
		value: PropertyValue,
	): Promise<ConfiguratorChange> {
		const update = fieldValueToPropertyUpdate(value);
		return this.set(property, update);
	}

	async getDomain(property: Property): Promise<PropertyValue[]> {
		const handler = this._getHandler(property);
		if (this._isMissingDefaultProperty(handler, property)) {
			return [];
		}
		const state = await handler.get(property);
		if ("domain" in state) {
			return propertyDomainToFieldValues(state.domain as Domain<PropertyValue>);
		} else {
			throw new Error(
				`Failed getting domain of property ${getQualifiedPropertyName(property)}`,
			);
		}
	}

	/**
	 * Synchronous counterpart to {@link getValue}, for callers that cannot await:
	 * React render bodies and the synchronous `_render` contract of the
	 * `@animech/axiom-ui-builder` component builders.
	 *
	 * Reads the configurator directly. Equivalent to {@link getValue} for every
	 * existing property handled by the default handler, whose `get` is a bare
	 * `return this._configurator.get(property)` — see the class doc-comment.
	 * Unlike {@link getValue}, it throws for a property that no longer exists: a
	 * stale index read during render is a real bug. Also throws rather than
	 * silently diverging if a custom async handler ever covers the property.
	 */
	getValueSync<TValue, TPath extends Path>(
		property: Property<TValue, TPath>,
	): TValue {
		this._assertDefaultHandler(property, "getValueSync");
		return this._configurator.get(property).value as TValue;
	}

	/** Synchronous counterpart to {@link getDomain}. See {@link getValueSync}. */
	getDomainSync(property: Property): PropertyValue[] {
		this._assertDefaultHandler(property, "getDomainSync");
		const state = this._configurator.get(property);
		if ("domain" in state) {
			return propertyDomainToFieldValues(state.domain as Domain<PropertyValue>);
		} else {
			throw new Error(
				`Failed getting domain of property ${getQualifiedPropertyName(property)}`,
			);
		}
	}

	// A field bound to an array element can read it once more after the element
	// is deleted, before it unmounts.
	private _isMissingDefaultProperty(
		handler: ConfigurationHandler,
		property: Property,
	): boolean {
		return (
			handler === this._defaultHandler && !this._configurator.exists(property)
		);
	}

	private _assertDefaultHandler(property: Property, method: string): void {
		if (this._getHandler(property) !== this._defaultHandler) {
			throw new Error(
				`${method} cannot be used on property ` +
					`${getQualifiedPropertyName(property)}: it is covered by a custom ` +
					`handler, which may be asynchronous. Use the awaited variant instead.`,
			);
		}
	}

	setDomain(
		property: Property,
		domain: PropertyValue[],
	): Promise<ConfiguratorChange> {
		const update = {
			domain: fieldDomainToPropertyUpdate(domain),
		} as PartialPropertyUpdateTree<Property>;
		return this.set(property, update);
	}

	resetConfiguration(): ConfiguratorChange {
		const change = this._configurator.reset();
		for (const listener of this._configurationChangeListeners) {
			listener(root, change);
		}
		return change;
	}

	addConfigurationChangeListener(listener: ConfigurationChangeListener): void {
		this._configurationChangeListeners.add(listener);
	}

	removeConfigurationChangeListener(
		listener: ConfigurationChangeListener,
	): void {
		this._configurationChangeListeners.delete(listener);
	}

	private _getHandler(property: Property): ConfigurationHandler {
		for (const [handlerRoot, handler] of this._customHandlers) {
			if (isInSubtree(property, handlerRoot)) {
				return handler;
			}
		}
		return this._defaultHandler;
	}
}
